// AI responder — turns an andon event into a Rule by asking BabyAI.
//
// The contract is intentionally narrow: prompt → JSON → validate against
// the DSL schema → Rule. Invalid output throws; the hybrid responder
// upstream decides how to fall back. This narrow contract IS the
// "closed action space" half of the architecture made literal — the
// AI cannot escape the DSL, even by accident, because invalid
// proposals get rejected at parse time before the sandbox even sees
// them.
//
// Every call also records itself to a lastCall ring so the AI Thinking
// panel can surface what the model just did.

import { callBabyAI, type BabyAIMessage } from "./babyai-client";
import {
  complexitySummary,
  formatRule,
  formatRuleset,
  MAX_LENGTH,
  nextComplexityHint,
  type CharClass,
  type Op,
  type Predicate,
  type Rule,
} from "./dsl";
import type { State } from "./andon-loop";

const ALLOWED_OPS: readonly Op[] = ["=", "!=", ">", ">=", "<", "<="];
const ALLOWED_CLASSES: readonly CharClass[] = ["digit", "lower", "upper", "letter", "symbol"];

// ─── Last-call telemetry (consumed by AiThinkingPanel) ──────────────────────

export interface AiCallRecord {
  ts: number;
  prompt: { system: string; user: string };
  responseText: string;
  /** Set when the JSON parsed and validated cleanly. */
  parsed: Rule | null;
  /** Set when something went wrong — invalid JSON, unknown predicate
   *  kind, type mismatch, etc. The hybrid responder uses this to
   *  decide whether to fall back to stub. */
  error: string | null;
  /** Routed model from the MoE response. */
  model: string | null;
  latencyMs: number;
}

let lastCall: AiCallRecord | null = null;
const lastCallListeners = new Set<(r: AiCallRecord | null) => void>();

export function getLastAiCall(): AiCallRecord | null {
  return lastCall;
}

export function subscribeLastAiCall(fn: (r: AiCallRecord | null) => void): () => void {
  lastCallListeners.add(fn);
  return () => lastCallListeners.delete(fn);
}

function setLastCall(rec: AiCallRecord | null): void {
  lastCall = rec;
  for (const fn of lastCallListeners) {
    try { fn(rec); } catch (e) { console.warn("[ai-responder] listener threw:", e); }
  }
}

// ─── Prompt composition ─────────────────────────────────────────────────────

const SYSTEM_PROMPT = `You are the AI half of an ANDON LOOP demo. A deterministic system maintains a password puzzle. When the player solves it, an andon cord pulls and YOU propose exactly ONE new rule to make the next puzzle slightly harder. Over many pulls the password should grow from a few characters into something long and elaborate — think of "The Password Game", except YOU write the rules.

You can only propose rules in this fixed DSL — invalid proposals are rejected by the harness before they land. The DSL is:

  { "kind": "LENGTH",           "op": "=|!=|>|>=|<|<=", "n": <integer> }
  { "kind": "CONTAINS",         "class": "digit|lower|upper|letter|symbol" }
  { "kind": "NOT_CONTAINS",     "pattern": "<literal substring>" }
  { "kind": "POSITION",         "i": <0-based index>, "class": "digit|lower|upper|letter|symbol" }
  { "kind": "SUM_OF_DIGITS",    "op": "=|!=|>|>=|<|<=", "n": <integer> }
  { "kind": "NO_REPEAT" }
  { "kind": "STARTS_WITH",      "class": "digit|lower|upper|letter|symbol" }
  { "kind": "NO_RUN_OF_LENGTH", "n": <integer 2 or greater> }

Good escalation is NOT "make it longer." It moves along several independent dimensions of difficulty. Walk them roughly in this order, adding the simplest rule that introduces a dimension the password doesn't have yet:

  1. Character variety: numeric -> allow letters (CONTAINS lower) -> demand mixed case (CONTAINS upper) -> require a symbol (CONTAINS symbol).
  2. Structure: NO_REPEAT, then NO_RUN_OF_LENGTH.
  3. Arithmetic: SUM_OF_DIGITS over the digits.
  4. Exclusions: NOT_CONTAINS specific patterns ("123", "00", a word).
  5. Positional: STARTS_WITH / POSITION pinning an index to a class.
  6. Length: raise it occasionally and by only 1-2 at a time.

LENGTH is a single RATCHET. Proposing a LENGTH rule doesn't add a second one — it raises the existing target. You may only raise it, never lower it, max ${MAX_LENGTH}. Length is just ONE dimension: do NOT raise it on consecutive turns, and don't reach for it while the password is still short on character variety or structure.

Rules:
- Output ONLY a single JSON object with shape { "name": "...", "predicate": {...} }. No prose, no markdown, no commentary.
- "name" is short snake_case, descriptive (e.g. "allow_letters", "no_repeat", "sum_at_least_10").
- Make it SLIGHTLY harder each time — not impossible. A proposal no password can satisfy is rejected at the sandbox gate and wastes the turn.
- Before requiring a character class at a position (e.g. uppercase at index 1), make sure the password is ALLOWED to contain it — add the matching CONTAINS rule first.
- Prefer a dimension the current rule set doesn't cover yet, and NEVER restate a rule that's already present. The user message lists what's covered and suggests the next move — follow it unless you have a clearly better idea.`;

function userPromptFor(state: Readonly<State>): string {
  const ruleset = state.rules.length === 0 ? "(empty)" : formatRuleset(state.rules);
  const last = state.guesses[state.guesses.length - 1];
  const winLine = last && last.passed
    ? `The player just guessed "${last.guess}" and won.`
    : `The cord was pulled manually (no winning guess).`;
  const coverage = complexitySummary(state.rules);
  const hint = nextComplexityHint(state.rules);
  return `Current rule set:
${ruleset}

Complexity covered so far: ${coverage}.
Suggested next move: ${hint}

The current password (for context — do not echo it back): ${state.currentPassword}

${winLine}

Propose the next rule.`;
}

// ─── JSON extraction + validation ───────────────────────────────────────────

function extractJsonObject(text: string): string | null {
  // Strip common code-fence wrappers, then find the first {...} balanced
  // chunk. Tolerant of extra prose around the JSON even though the
  // system prompt says "no prose."
  const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenceMatch) return fenceMatch[1].trim();
  // Find first balanced { ... } in the text.
  const start = text.indexOf("{");
  if (start < 0) return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escape) { escape = false; continue; }
      if (ch === "\\") { escape = true; continue; }
      if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') { inString = true; continue; }
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

function isOp(x: unknown): x is Op {
  return typeof x === "string" && (ALLOWED_OPS as readonly string[]).includes(x);
}
function isClass(x: unknown): x is CharClass {
  return typeof x === "string" && (ALLOWED_CLASSES as readonly string[]).includes(x);
}
function isInt(x: unknown): x is number {
  return typeof x === "number" && Number.isInteger(x);
}

/** Validate the parsed JSON against the Predicate schema. Returns the
 *  narrowed Predicate or throws with a precise reason. */
function validatePredicate(raw: unknown): Predicate {
  if (!raw || typeof raw !== "object") throw new Error("predicate is not an object");
  const p = raw as Record<string, unknown>;
  const kind = p.kind;
  switch (kind) {
    case "LENGTH":
      if (!isOp(p.op)) throw new Error("LENGTH.op is not a valid op");
      if (!isInt(p.n)) throw new Error("LENGTH.n is not an integer");
      return { kind: "LENGTH", op: p.op, n: p.n };
    case "CONTAINS":
      if (!isClass(p.class)) throw new Error("CONTAINS.class is not a valid class");
      return { kind: "CONTAINS", class: p.class };
    case "NOT_CONTAINS":
      if (typeof p.pattern !== "string" || p.pattern.length === 0) {
        throw new Error("NOT_CONTAINS.pattern must be a non-empty string");
      }
      return { kind: "NOT_CONTAINS", pattern: p.pattern };
    case "POSITION":
      if (!isInt(p.i) || p.i < 0) throw new Error("POSITION.i must be a non-negative integer");
      if (!isClass(p.class)) throw new Error("POSITION.class is not a valid class");
      return { kind: "POSITION", i: p.i, class: p.class };
    case "SUM_OF_DIGITS":
      if (!isOp(p.op)) throw new Error("SUM_OF_DIGITS.op is not a valid op");
      if (!isInt(p.n)) throw new Error("SUM_OF_DIGITS.n is not an integer");
      return { kind: "SUM_OF_DIGITS", op: p.op, n: p.n };
    case "NO_REPEAT":
      return { kind: "NO_REPEAT" };
    case "STARTS_WITH":
      if (!isClass(p.class)) throw new Error("STARTS_WITH.class is not a valid class");
      return { kind: "STARTS_WITH", class: p.class };
    case "NO_RUN_OF_LENGTH":
      if (!isInt(p.n) || p.n < 2) throw new Error("NO_RUN_OF_LENGTH.n must be an integer >= 2");
      return { kind: "NO_RUN_OF_LENGTH", n: p.n };
    default:
      throw new Error(`unknown predicate kind: ${JSON.stringify(kind)}`);
  }
}

function validateRule(raw: unknown, ruleCount: number): Rule {
  if (!raw || typeof raw !== "object") throw new Error("response is not an object");
  const r = raw as Record<string, unknown>;
  const name = typeof r.name === "string" && r.name.length > 0 ? r.name : `ai_rule_${ruleCount}`;
  const sanitizedName = name.replace(/[^a-z0-9_]/gi, "_").toLowerCase().slice(0, 40) || `ai_rule_${ruleCount}`;
  const predicate = validatePredicate(r.predicate);
  return {
    id: `r_${ruleCount}_${sanitizedName}`,
    name: sanitizedName,
    predicate,
  };
}

// ─── The responder ──────────────────────────────────────────────────────────

export async function aiResponder(state: Readonly<State>): Promise<Rule> {
  const t0 = performance.now();
  const system = SYSTEM_PROMPT;
  const user = userPromptFor(state);
  const messages: BabyAIMessage[] = [
    { role: "system", content: system },
    { role: "user",   content: user   },
  ];

  let responseText = "";
  let model: string | null = null;
  try {
    const result = await callBabyAI({ messages });
    responseText = result.text;
    model = result.model;
  } catch (e) {
    const err = e instanceof Error ? e.message : String(e);
    setLastCall({
      ts: Date.now(),
      prompt: { system, user },
      responseText: "",
      parsed: null,
      error: `transport: ${err}`,
      model: null,
      latencyMs: performance.now() - t0,
    });
    throw e;
  }

  // Parse + validate.
  try {
    const json = extractJsonObject(responseText);
    if (!json) throw new Error("no JSON object found in response");
    const parsed = JSON.parse(json);
    const rule = validateRule(parsed, state.rules.length);
    setLastCall({
      ts: Date.now(),
      prompt: { system, user },
      responseText,
      parsed: rule,
      error: null,
      model,
      latencyMs: performance.now() - t0,
    });
    // Surface a hint via formatRule for the panel (handy when debugging).
    console.info(`[ai-responder] proposed: ${formatRule(rule)} (model=${model})`);
    return rule;
  } catch (e) {
    const err = e instanceof Error ? e.message : String(e);
    setLastCall({
      ts: Date.now(),
      prompt: { system, user },
      responseText,
      parsed: null,
      error: `parse/validate: ${err}`,
      model,
      latencyMs: performance.now() - t0,
    });
    throw e;
  }
}
